import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Loader2, Mail, MailCheck, MailOpen, MailX, MousePointerClick } from 'lucide-react';
import { parseInstantDate } from '../lib/calendar-date';
import { effectiveTimeZone } from '../lib/tenant-zone';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';

export type TicketCustomerEmailStatus =
  | 'not_sent' | 'pending' | 'sent' | 'failed'
  | 'bounced' | 'delivered' | 'opened' | 'clicked' | 'complained';

interface EmailEngagementPillProps {
  status: TicketCustomerEmailStatus;
  errorMessage?: string | null;
  lastAttemptAt?: string | null;
  deliveredAt?: string | null;
  openCount?: number;
  lastOpenedAt?: string | null;
  clickCount?: number;
  lastClickedAt?: string | null;
  complainedAt?: string | null;
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

/**
 * When the customer opened the email, on the TENANT's clock
 * (companies.time_zone) — not the viewer's device. The format is unchanged
 * (the viewer's locale, date and time); only the zone it is read in moved.
 * An em dash for an absent or unreadable instant, or an unknown zone.
 */
function fmtDate(iso: string | null | undefined, timeZone: string | null): string {
  const at = parseInstantDate(iso);
  if (!at || !timeZone) return '\u2014';
  try {
    return at.toLocaleString(undefined, { timeZone: effectiveTimeZone(timeZone, at) });
  } catch {
    return '\u2014'; // an unrecognised zone throws RangeError
  }
}

function getMostRecentTimestamp(props: EmailEngagementPillProps): string | null {
  const candidates = [
    props.lastClickedAt,
    props.lastOpenedAt,
    props.deliveredAt,
    props.lastAttemptAt,
  ].filter(Boolean) as string[];
  if (candidates.length === 0) return null;
  return candidates.reduce((latest, c) => (new Date(c) > new Date(latest) ? c : latest));
}

/**
 * THE PILL IS A BADGE, so it takes the badge idiom: a saturated `--fi-signal-*`
 * fill with `text-fi-signal-ink` on it and a black/20 edge.
 *
 * It was a 15% wash with the bright literal ink on top — the exact pairing
 * CLAUDE.md calls out, and the one that fails in light mode only: the washes go
 * nearly white while the literal shades do not move, so the words were around
 * 3:1 on a white panel and nothing reported it. The signal hues do not follow
 * the theme either, which is why one dark ink holds on all four.
 */
export default function EmailEngagementPill(props: EmailEngagementPillProps) {
  const { status, errorMessage, deliveredAt, openCount = 0, lastOpenedAt, clickCount = 0, lastClickedAt, complainedAt } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  const timeZone = useTenantTimeZone();

  let pillBg = '';
  let pillText = '';
  let pillBorder = '';
  let label = '';
  let tooltip = '';
  let Icon: React.ElementType = Mail;
  let pulse = false;

  switch (status) {
    case 'complained':
      pillBg = 'bg-fi-signal-red';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = AlertTriangle;
      label = 'Marked as Spam';
      tooltip = complainedAt ? `Customer reported as spam ${relTime(complainedAt)}` : 'Customer reported as spam';
      break;

    case 'bounced':
      pillBg = 'bg-fi-signal-red';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = MailX;
      label = 'Bounced';
      tooltip = errorMessage || 'Email bounced — address may be invalid';
      break;

    case 'failed':
      pillBg = 'bg-fi-signal-red';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = MailX;
      label = 'Send Failed';
      tooltip = errorMessage || 'Email delivery failed';
      break;

    case 'clicked':
      pillBg = 'bg-fi-signal-green';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = MousePointerClick;
      if (clickCount <= 1 && openCount <= 1) {
        label = 'Opened & Clicked';
      } else if (clickCount <= 1) {
        label = `Read ${openCount}x \u00b7 Clicked`;
      } else {
        label = `Read ${openCount}x \u00b7 Clicked ${clickCount}x`;
      }
      tooltip = `Last opened ${relTime(lastOpenedAt)}, last clicked ${relTime(lastClickedAt)}`;
      break;

    case 'opened':
      pillBg = 'bg-fi-signal-green';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = MailOpen;
      label = openCount > 1 ? `Opened ${openCount}x` : 'Opened';
      tooltip = lastOpenedAt
        ? `First opened ${fmtDate(lastOpenedAt, timeZone)}, last opened ${relTime(lastOpenedAt)}`
        : 'Email has been opened';
      break;

    case 'delivered':
      pillBg = 'bg-fi-signal-green';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = MailCheck;
      label = 'Delivered';
      tooltip = deliveredAt
        ? `Delivered ${relTime(deliveredAt)} \u2014 recipient hasn't opened yet`
        : 'Delivered \u2014 recipient hasn\'t opened yet';
      break;

    case 'sent':
      pillBg = 'bg-fi-signal-amber';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = Mail;
      label = 'Sent';
      tooltip = 'Awaiting delivery confirmation';
      pulse = true;
      break;

    case 'pending':
      pillBg = 'bg-fi-signal-amber';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = Loader2;
      label = 'Sending...';
      tooltip = 'Email is being sent';
      break;

    case 'not_sent':
    default:
      pillBg = 'bg-fi-signal-slate';
      pillText = 'text-fi-signal-ink';
      pillBorder = 'border-black/20';
      Icon = Mail;
      label = 'Not Sent';
      tooltip = 'Email has not been sent to the customer';
      break;
  }

  const mostRecent = getMostRecentTimestamp(props);

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <div
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span
          data-testid="email-engagement-pill"
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${pillBg} ${pillText} ${pillBorder} ${pulse ? 'animate-pulse' : ''}`}
        >
          <Icon size={12} className={status === 'pending' ? 'animate-spin' : ''} />
          {label}
        </span>

        {showTooltip && tooltip && (
          <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-fieldiq-dark border border-fieldiq-border rounded-lg shadow-xl text-xs text-fieldiq-text whitespace-nowrap max-w-xs">
            {tooltip}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-fieldiq-border" />
          </div>
        )}
      </div>

      {mostRecent && status !== 'not_sent' && status !== 'pending' && (
        <span className="text-[10px] text-fieldiq-muted pl-1">
          Last activity: {relTime(mostRecent)}
        </span>
      )}
    </div>
  );
}

/**
 * Compact text-only variant for ticket list rows.
 *
 * THIS ONE IS INK ON A THEME SURFACE, NOT A FILL, so none of the signal hues
 * can be used: they have no light twin by design, so as a text colour they keep
 * their bright dark-mode value on a white panel. The red arm read about 3:1
 * there (fe#730 found it, fe#733's successor fixes it). It is now the theme's
 * own ink with weight doing the work, and the accent's SIGNAL half for the
 * amber arm — which is the one token remapped for exactly this purpose.
 *
 * The status is already named in words, so the colour was never the only
 * carrier of the message.
 */
export function EmailStatusIndicator({ status, openCount, lastOpenedAt }: { status: TicketCustomerEmailStatus; openCount?: number; lastOpenedAt?: string | null }) {
  if (!status || status === 'not_sent') return null;

  const hasBeenOpened = (openCount != null && openCount > 0) || lastOpenedAt != null;

  let text = '';
  let className = 'text-fieldiq-muted';

  switch (status) {
    case 'pending':
      text = 'Sending...';
      className = 'text-fieldiq-accent-signal';
      break;
    case 'sent':
      text = 'Sent';
      className = 'text-fieldiq-accent-signal';
      break;
    case 'delivered':
      text = 'Sent \u2713';
      className = 'text-fieldiq-text';
      break;
    case 'opened':
      if (hasBeenOpened) {
        text = 'Read';
        className = 'text-fieldiq-text';
      } else {
        // TODO: API may report 'opened' without populating open_count/last_opened_at — update API to return these fields on GET /tickets
        console.warn('EmailStatusIndicator: status is "opened" but open tracking fields are missing');
        text = 'Sent';
        className = 'text-fieldiq-accent-signal';
      }
      break;
    case 'clicked':
      if (hasBeenOpened) {
        text = 'Read & Clicked';
        className = 'text-fieldiq-text';
      } else {
        text = 'Sent';
        className = 'text-fieldiq-accent-signal';
      }
      break;
    case 'failed':
      text = 'Failed';
      className = 'text-fieldiq-text font-semibold';
      break;
    case 'bounced':
      text = 'Bounced \u26a0';
      className = 'text-fieldiq-text font-semibold';
      break;
    case 'complained':
      text = 'Spam \u26a0';
      className = 'text-fieldiq-text font-semibold';
      break;
  }

  return (
    <span data-testid="email-status-indicator" className={`text-xs ${className}`}>{' \u00b7 '}{text}</span>
  );
}
